/**
 * Reads and writes for the board column registry.
 *
 * TWO THINGS SHAPE EVERY MUTATION HERE.
 *
 * 1. A column write CASCADES. Deleting one re-homes its issues onto another
 *    column and can promote a new default; re-mapping one rewrites the legacy
 *    status of every card inside it. So the server answers each write with the
 *    WHOLE set, and the success handler replaces the cache outright instead of
 *    merging a row into it.
 *
 * 2. The board is a second view of the same rows. Every column write therefore
 *    invalidates the board, the backlog and every issue list — otherwise a
 *    renamed column keeps its old name on the board until something else
 *    happens to refetch it.
 *
 * The optimistic updates cover the edits that are purely local to one row —
 * rename, WIP limit, default flag — and the reorder, which is the one place
 * where waiting for a round trip is visible as the row springing back. Each
 * takes a snapshot first and puts it back on failure, so a refusal the server
 * owns (the last column of a status, a plan limit) leaves the screen showing
 * what is actually stored.
 */

import { useCallback } from 'react'
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'

import {
  issueKeys,
  v2Api,
  type ColumnCreateInput,
  type ColumnPatchInput,
  type ColumnRow,
  type ColumnsResponse,
  type TaskStatus,
} from '@/features/issue'
import type { UUID } from '@/types/api'

/** Every cached view that would show a stale column name or count. */
function invalidateBoardViews(client: QueryClient, slug: string) {
  void client.invalidateQueries({ queryKey: issueKeys.boards(slug) })
  void client.invalidateQueries({ queryKey: issueKeys.backlogs(slug) })
  void client.invalidateQueries({ queryKey: issueKeys.issueLists(slug) })
}

export function useColumnsQuery(slug: string | null, projectKey: string | null) {
  return useQuery({
    queryKey: issueKeys.columns(slug ?? '', projectKey ?? ''),
    queryFn: () => v2Api.columns(slug as string, projectKey as string),
    enabled: Boolean(slug && projectKey),
    staleTime: 30_000,
  })
}

/** Recomputes the per-status summary after an optimistic edit. */
function recountStatuses(response: ColumnsResponse, columns: ColumnRow[]): ColumnsResponse {
  const statuses = response.statuses.map((entry) => {
    const members = columns.filter((column) => column.legacy_status === entry.status)
    const preferred = members.find((column) => column.is_default_for_status) ?? members[0]
    return { ...entry, columns: members.length, default_column_id: preferred?.id }
  })
  return { ...response, data: columns, statuses }
}

export interface ColumnMutations {
  create: ReturnType<typeof useCreateColumn>
  patch: ReturnType<typeof usePatchColumn>
  remove: ReturnType<typeof useDeleteColumn>
  reorder: ReturnType<typeof useReorderColumns>
}

function useCreateColumn(slug: string | null, projectKey: string | null) {
  const client = useQueryClient()
  const key = issueKeys.columns(slug ?? '', projectKey ?? '')

  return useMutation({
    mutationFn: (input: ColumnCreateInput) =>
      v2Api.createColumn(slug as string, projectKey as string, input),
    // Not optimistic on purpose: a create is the one write that can be refused
    // by the PLAN, and showing a column that then vanishes is worse than a
    // half-second wait.
    onSuccess: (response) => {
      client.setQueryData(key, response)
      if (slug) invalidateBoardViews(client, slug)
    },
  })
}

export interface ColumnPatch {
  id: UUID
  input: ColumnPatchInput
}

function usePatchColumn(slug: string | null, projectKey: string | null) {
  const client = useQueryClient()
  const key = issueKeys.columns(slug ?? '', projectKey ?? '')

  return useMutation({
    mutationFn: ({ id, input }: ColumnPatch) =>
      v2Api.patchColumn(slug as string, projectKey as string, id, input),
    onMutate: async ({ id, input }) => {
      await client.cancelQueries({ queryKey: key })
      const snapshot = client.getQueryData<ColumnsResponse>(key)
      if (snapshot) {
        const target = snapshot.data.find((column) => column.id === id)
        // Promoting a default demotes its siblings in the same breath, and the
        // status they share is the column's OWN one — the patch body carries it
        // only when the status is changing too.
        const promotedStatus = input.is_default_for_status
          ? (input.legacy_status ?? target?.legacy_status)
          : undefined
        const columns = snapshot.data.map((column) => {
          if (column.id === id) {
            return {
              ...column,
              ...(input.name !== undefined ? { name: input.name } : {}),
              ...(input.legacy_status !== undefined
                ? { legacy_status: input.legacy_status }
                : {}),
              ...(input.wip_limit !== undefined ? { wip_limit: input.wip_limit } : {}),
              ...(input.is_default_for_status !== undefined
                ? { is_default_for_status: input.is_default_for_status }
                : {}),
            }
          }
          if (promotedStatus && column.legacy_status === promotedStatus) {
            return { ...column, is_default_for_status: false }
          }
          return column
        })
        client.setQueryData<ColumnsResponse>(key, recountStatuses(snapshot, columns))
      }
      return { snapshot }
    },
    onError: (_error, _variables, context) => {
      if (context?.snapshot) client.setQueryData(key, context.snapshot)
    },
    onSuccess: (response) => {
      client.setQueryData(key, response)
    },
    onSettled: () => {
      void client.invalidateQueries({ queryKey: key })
      if (slug) invalidateBoardViews(client, slug)
    },
  })
}

export interface ColumnDeletion {
  id: UUID
  /** The column its issues land in. Omitted, the server picks the status default. */
  moveTo?: UUID | null
}

function useDeleteColumn(slug: string | null, projectKey: string | null) {
  const client = useQueryClient()
  const key = issueKeys.columns(slug ?? '', projectKey ?? '')

  return useMutation({
    mutationFn: ({ id, moveTo }: ColumnDeletion) =>
      v2Api.deleteColumn(slug as string, projectKey as string, id, moveTo ?? null),
    // Also not optimistic: a delete moves issues between columns, and guessing
    // where they landed would show counts the server never agreed to.
    onSuccess: (response) => {
      client.setQueryData(key, response)
      if (slug) invalidateBoardViews(client, slug)
    },
  })
}

function useReorderColumns(slug: string | null, projectKey: string | null) {
  const client = useQueryClient()
  const key = issueKeys.columns(slug ?? '', projectKey ?? '')

  return useMutation({
    mutationFn: (ids: UUID[]) => v2Api.reorderColumns(slug as string, projectKey as string, ids),
    onMutate: async (ids) => {
      await client.cancelQueries({ queryKey: key })
      const snapshot = client.getQueryData<ColumnsResponse>(key)
      if (snapshot) {
        const byId = new Map(snapshot.data.map((column) => [column.id, column]))
        const columns = ids
          .map((id, index) => {
            const column = byId.get(id)
            return column ? { ...column, position: index } : null
          })
          .filter((column): column is ColumnRow => column !== null)
        if (columns.length === snapshot.data.length) {
          client.setQueryData<ColumnsResponse>(key, { ...snapshot, data: columns })
        }
      }
      return { snapshot }
    },
    onError: (_error, _ids, context) => {
      if (context?.snapshot) client.setQueryData(key, context.snapshot)
    },
    onSuccess: (response) => {
      client.setQueryData(key, response)
    },
    onSettled: () => {
      void client.invalidateQueries({ queryKey: key })
      if (slug) invalidateBoardViews(client, slug)
    },
  })
}

export function useColumnMutations(slug: string | null, projectKey: string | null): ColumnMutations {
  return {
    create: useCreateColumn(slug, projectKey),
    patch: usePatchColumn(slug, projectKey),
    remove: useDeleteColumn(slug, projectKey),
    reorder: useReorderColumns(slug, projectKey),
  }
}

/**
 * The last error any column write produced, and a way to dismiss it.
 *
 * One banner rather than four: the messages are the server's own sentences —
 * "…is the only To do column", "Custom board columns are a Taskly Pro feature"
 * — and only one of them can be about the thing the person just did. The two
 * dialogs show their own errors inline, so this covers the inline edits and the
 * reorder.
 */
export function useLastColumnError(mutations: ColumnMutations) {
  const { patch, reorder } = mutations
  const error = patch.error ?? reorder.error ?? null

  const clear = useCallback(() => {
    patch.reset()
    reorder.reset()
  }, [patch, reorder])

  return { error, clear }
}

/** How many columns map to a status, from the server's own summary. */
export function columnsForStatus(response: ColumnsResponse | undefined, status: TaskStatus): number {
  return response?.statuses.find((entry) => entry.status === status)?.columns ?? 0
}
